import 'chart.js/auto' // React 18 duplicate chart error fix

import React, { Dispatch, SetStateAction, useRef } from 'react'
import Chart, { ScriptableContext } from 'chart.js/auto'
import moment from 'moment'
import styled from 'styled-components'
import { GLOBAL_COLORS, colors } from 'theme'
import { secondsToMills } from 'utils'
import { tooltipLine } from './tooltip-line'
import { useStore } from 'services/store'
import { Fonts, Weight } from 'components/typography'
interface ProductChartProps {
  chartData: { x: string; y: string }[]
  indexCallback: Dispatch<SetStateAction<number>>
  mouseOut: Dispatch<SetStateAction<boolean>>
  isPortfolio?: boolean
  productSymbol?: string
  padding?: number
  children?: JSX.Element
}

const Wrapper = styled.div``

/**
 * Sorting function for sorting arr timelines by date
 */

export const byXAxis = (a: { x: string }, b: { x: string }) => {
  if (a.x < b.x) return -1
  if (a.x > b.x) return 1
  return 0
}

const InvestIndexChartComponent: React.FC<ProductChartProps> = ({
  chartData,
  indexCallback,
  mouseOut,
}) => {
  const { isDarkMode } = useStore()

  const priceData = {
    data: chartData.map((e) => +e.y),
    pointBackgroundColor: GLOBAL_COLORS.seashellWhite,
    borderWidth: 2,
    borderColor: GLOBAL_COLORS.coralBlue,
    fill: true,
    backgroundColor: (context: ScriptableContext<'line'>) => {
      const { ctx, chartArea } = context.chart
      if (chartArea) {
        const gradientBg = ctx.createLinearGradient(0, chartArea.top, 0, chartArea.bottom)
        gradientBg.addColorStop(0, 'rgba(0, 56, 255, 0.5)')
        gradientBg.addColorStop(1, colors(isDarkMode).bg)
        return gradientBg
      }
    },
    tension: 0.1,
    yAxisID: 'y1',
    order: 1,
  }

  const dataset = [priceData]

  const chartRef = useRef<Chart | null>(null)
  const canvasCallback = (canvas: HTMLCanvasElement | null) => {
    if (!canvas) return
    const ctx = canvas.getContext('2d')
    if (ctx) {
      chartRef.current?.destroy()
      chartRef.current = new Chart(ctx, {
        type: 'line',
        data: {
          labels: chartData.map((e) => moment(secondsToMills(Number(e.x))).format('MMMM D, HH:mm')),
          datasets: dataset,
        },
        options: {
          animation: {
            duration: 0,
          },
          layout: {
            padding: 10,
          },
          hover: {
            mode: 'nearest',
            intersect: true,
          },
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            legend: { display: false },
            tooltip: {
              mode: 'index',
              intersect: false,
              yAlign: 'center',
              displayColors: false,
              caretPadding: 10,
              caretSize: 8,
              backgroundColor: colors(isDarkMode).bg,
              titleColor: colors(isDarkMode).text1,
              borderColor: colors(isDarkMode).bg,
              borderWidth: 1,
              cornerRadius: 10,
              padding: 12,
              titleMarginBottom: 0,
              titleFont: {
                size: 12,
                family: Fonts.DMSans,
                weight: Number(Weight.Medium),
              },
              callbacks: {
                label: () => '',
                footer: () => '',
                afterFooter: (e) => {
                  indexCallback(e[0].dataIndex)
                  return ''
                },
              },
            },
          },
          elements: {
            point: {
              radius: 0,
            },
          },
          scales: {
            x: { display: false },
            y1: {
              display: false,
              suggestedMax: undefined,
              suggestedMin: undefined,
            },
            y2: {
              display: false,
              suggestedMax: undefined,
              suggestedMin: undefined,
              ticks: {
                stepSize: 1,
              },
            },
          },
        },

        plugins: [
          tooltipLine(isDarkMode),
          {
            id: 'myEventCatcher',
            beforeEvent(chart, args) {
              const event = args.event

              if (event.type === 'mouseout') {
                mouseOut(true)
              }
              if (event.type === 'mousemove') {
                mouseOut(false)
              }
            },
          },
        ],
      })
    }
  }

  return (
    <Wrapper>
      <canvas style={{ maxWidth: '100%' }} width={'650px'} ref={canvasCallback}></canvas>
    </Wrapper>
  )
}

export const ProductChart: React.FC<ProductChartProps> = React.memo(
  InvestIndexChartComponent,
  (prevProps, nextProps) => {
    // render if one or other is empty
    if (prevProps.chartData.length === 0 || nextProps.chartData.length === 0) {
      return false
    }

    // otherwise, compare start and end x/y values and lengths
    return (
      prevProps.chartData[0].x === nextProps.chartData[0].x &&
      prevProps.chartData[0].y === nextProps.chartData[0].y &&
      prevProps.chartData[prevProps.chartData.length - 1].x ===
        nextProps.chartData[nextProps.chartData.length - 1].x &&
      prevProps.chartData[prevProps.chartData.length - 1].y ===
        nextProps.chartData[nextProps.chartData.length - 1].y &&
      prevProps.chartData.length === nextProps.chartData.length
    )
  },
)
